import logger from '~/services/logger';

export function setupMockDateOnce(
  isMock: boolean,
  mockDate: string,
  isMockModeInitializedRef: React.MutableRefObject<boolean>
) {
  if (isMock && !isMockModeInitializedRef.current) {
    const fakeNow = new Date(mockDate).valueOf();

    // Extend Date constructor to default to fakeNow
    // eslint-disable-next-line no-global-assign
    const OriginalDate = globalThis.Date;

    globalThis.Date = new Proxy(OriginalDate, {
      construct(target, args) {
        if (args.length === 0) {
          return new target(fakeNow);
        }
        return new target(
          ...(args as unknown as [
            number,
            number,
            number,
            number?,
            number?,
            number?,
            number?
          ])
        );
      },
    }) as DateConstructor;

    // Override Date.now() to start from fakeNow
    const __DateNowOffset = fakeNow - Date.now();
    const __DateNow = Date.now;
    Date.now = () => __DateNow() + __DateNowOffset;

    isMockModeInitializedRef.current = true;
  }
}

/**
 * Converts a Date object to an ISO date string.
 * @param date The Date object to convert. Defaults to the current date and time.
 * @returns The ISO date string representation of the given date.
 */
export function getIsoDateString(date: Date = new Date()): string {
  return date.toISOString();
}

/**
 * Normalizes an ISO date to the top of the hour in ISO format.
 * @param input the input object.
 * @param input.isoDate the ISO date to normalize.
 * @returns the normalized ISO date string.
 */
export function getNormalizedEpgDateTime({
  isoDate,
}: {
  isoDate: string;
}): string {
  try {
    const baseDate = new Date(isoDate);

    if (Number.isNaN(baseDate.getTime())) {
      throw new Error('Invalid ISO date');
    }

    return new Date(baseDate.setMinutes(0, 0, 0)).toISOString();
  } catch (error: unknown) {
    logger.error(`getNormalizedEpgDateTime: ${String(error)}`);
    return new Date().toISOString();
  }
}

/** Day width planby uses when no time slot interval is configured. */
export const EPG_DEFAULT_DAY_WIDTH = 7200;

/** Width (px) of a single timeline slot when a time slot interval is set. */
export const EPG_TIME_SLOT_WIDTH = 300;

const MAX_TIME_SLOT_INTERVAL = 120;

// invalid intervals already warned about, so a misconfigured screen logs once per value
const warnedTimeSlotIntervals = new Set<string>();

/**
 * Resolves the timeline slot interval (in minutes) from the EPG rules.
 * @param rules the EPG component rules.
 * @returns the interval in minutes, or null to keep the default timeline.
 */
export function getEpgTimeSlotInterval(rules: any): number | null {
  const timeInterval: string | undefined = rules?.time_interval;

  if (!timeInterval || timeInterval === 'default') return null;

  const rawValue: unknown =
    timeInterval === 'other' ? rules?.time_interval_custom : timeInterval;
  const value: number = Number(rawValue);

  if (
    !Number.isInteger(value) ||
    value <= 0 ||
    value > MAX_TIME_SLOT_INTERVAL
  ) {
    const warningKey: string = String(rawValue);

    if (!warnedTimeSlotIntervals.has(warningKey)) {
      warnedTimeSlotIntervals.add(warningKey);
      logger.warn(
        `getEpgTimeSlotInterval: invalid time slot interval "${rawValue}" (expected a whole number of minutes between 1 and ${MAX_TIME_SLOT_INTERVAL}), using the default timeline`
      );
    }

    return null;
  }

  return value;
}

/**
 * Gets the planby day width so that every timeline slot is EPG_TIME_SLOT_WIDTH wide.
 * @param input the input object.
 * @param input.startDate the EPG start date (ISO string).
 * @param input.timeSlotInterval the slot interval in minutes, or null.
 * @returns the day width in pixels.
 */
export function getEpgDayWidth({
  startDate,
  timeSlotInterval,
}: {
  startDate: string;
  timeSlotInterval: number | null;
}): number {
  if (!timeSlotInterval) return EPG_DEFAULT_DAY_WIDTH;

  const start = new Date(startDate);

  if (Number.isNaN(start.getTime())) return EPG_DEFAULT_DAY_WIDTH;

  // planby renders from startDate until the start of the next (local) day
  const end = new Date(start);
  end.setHours(24, 0, 0, 0);

  const numberOfHours: number = Math.max(
    1,
    Math.trunc((end.getTime() - start.getTime()) / 3_600_000)
  );

  const hourWidth: number = Math.round(
    (EPG_TIME_SLOT_WIDTH * 60) / timeSlotInterval
  );

  return hourWidth * numberOfHours;
}
